<script setup lang="ts">
import { defineProps, onMounted, onUnmounted, Ref, ref } from 'vue';
import UploadCloudIcon from './UploadCloudIcon.vue';

const props = defineProps<{
  uploading: boolean;
  uploadedCount: number;
  allCount: number;
  progress: number;
  startUpload: (files: FileList) => void;
}>();

const inputFile: Ref<HTMLInputElement | undefined> = ref(undefined);
const cloud: Ref<HTMLDivElement | undefined> = ref(undefined);
const isDragging: Ref<boolean> = ref(false);

function uploadDraftClick() {
  if (!props.uploading && inputFile.value) {
    inputFile.value.click();
  }
}

function inputFileChanged() {
  if (inputFile.value) {
    const files = inputFile.value.files;

    if (!!files && files.length > 0) {
      props.startUpload(files);
      inputFile.value.value = '';
    }
  }
}

function onDrop(event: DragEvent) {
  event.preventDefault();
  isDragging.value = false;

  if (props.uploading) {
    return;
  }

  const dataTransfer = new DataTransfer();
  const types = ['image/*', 'video/*'];

  if (event.dataTransfer) {
    const files = event.dataTransfer.files;

    for (let i = 0; i < files.length; i++) {
      const item = files[i];

      // const isAccepted = types.some((type) => {
      //   if (type.endsWith('/*')) {
      //     // Handle wildcard types like 'image/*'
      //     return item.type.startsWith(type.split('/')[0] + '/');
      //   } else {
      //     // Handle specific types
      //     return item.type === type;
      //   }
      // });
      const isAccepted = true;

      if (isAccepted) {
        dataTransfer.items.add(item);
      }
    }
  }

  if (inputFile.value) {
    inputFile.value.files = dataTransfer.files;
    inputFile.value.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function onDragOver(event: DragEvent) {
  event.preventDefault();

  if (!isDragging.value && !props.uploading) {
    isDragging.value = true;
  }

  if (isDragging.value && cloud.value) {
    const mouseY = event.clientY;
    const mouseX = event.clientX;

    cloud.value.style.transform = `translate3d(${mouseX - 123}px, ${mouseY - 92}px, 0)`;
  }
}

function onDragleave(event: DragEvent) {
  event.preventDefault();
  const target: Element | undefined = event.target as Element | undefined;

  if (target && target.classList.contains('drag-zone')) {
    isDragging.value = false;
  }
}

onMounted(() => {
  document.addEventListener('drop', onDrop);
  document.addEventListener('dragover', onDragOver);
  document.addEventListener('dragleave', onDragleave, true);
});

onUnmounted(() => {
  document.removeEventListener('drop', onDrop);
  document.removeEventListener('dragover', onDragOver);
  document.removeEventListener('dragleave', onDragleave);
});
</script>

<template>
  <div ref="dragZone" class="drag-zone bg-gray-100 dark:bg-gray-975" :class="{ show: isDragging }">
    <div ref="cloud" class="cloud">
      <UploadCloudIcon class="stroke-black dark:stroke-gray-600" />
      <p class="text-caption-s mt-s text-center text-black dark:text-gray-600">
        Drag here to upload or <br />
        click ESC to cancel.
      </p>
    </div>
  </div>

  <div class="main-drafts__upload h-[280px] max-tablet:mb-[7px] max-tablet:h-[88px]" :class="{ uploading: uploading }" @click="uploadDraftClick">
    <input ref="inputFile" type="file" multiple @change="inputFileChanged" />

    <div v-if="!uploading" class="relative z-[1] flex flex-col items-center justify-center pt-xxs">
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="mb-s stroke-black dark:stroke-gray-600 max-tablet:relative max-tablet:bottom-xxs max-tablet:mb-zero"
      >
        <path
          d="M14.4819 12.9315L14.4828 12.9306C14.4987 12.9154 14.5039 12.8993 14.5039 12.8846C14.5039 12.8699 14.4987 12.8538 14.4828 12.8386L14.4819 12.8377L12.0819 10.53C12.0627 10.5115 12.0355 10.5 12.0037 10.5C11.9696 10.5 11.9442 10.5118 11.9273 10.5283L11.9254 10.53L9.52547 12.8377L9.52455 12.8386C9.50864 12.8538 9.50342 12.8699 9.50342 12.8846C9.50342 12.8993 9.50864 12.9154 9.52455 12.9306L9.52547 12.9315C9.54343 12.9488 9.5703 12.9607 9.60311 12.9607C9.63591 12.9607 9.66279 12.9488 9.68074 12.9315L11.0559 11.6092L11.9025 10.7952V11.9696V20.9231C11.9025 20.9386 11.9081 20.9549 11.9246 20.9707C11.9415 20.987 11.968 21 12.0025 21C12.0369 21 12.0634 20.987 12.0804 20.9707C12.0968 20.9549 12.1025 20.9386 12.1025 20.9231V11.9696V10.7952L12.949 11.6092L14.3242 12.9315C14.3416 12.9483 14.3688 12.9607 14.403 12.9607C14.4372 12.9607 14.4644 12.9483 14.4819 12.9315Z"
        />
        <path
          d="M8.75296 16.9997H5.77731C3.70008 16.9997 2 15.2161 2 13.0374C2 10.8581 3.70008 9.07513 5.77731 9.07513H5.85751V8.11444C5.85751 5.30127 8.09658 3 10.8334 3C13.5112 3 15.7125 5.2031 15.8064 7.93303H17.9321C20.1697 7.93303 22 9.85015 22 12.1981V12.7316C22 15.0789 20.1697 17 17.9321 17H15.4833"
          stroke-miterlimit="10"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>

      <p class="text-caption-s hidden text-center text-black dark:text-gray-600 laptop:block">
        Browse <br />
        or drag & drop here <br />
        to upload
      </p>
      <p class="text-caption-s hidden text-center text-black dark:text-gray-600 max-tablet:block">Upload</p>
    </div>

    <div v-if="uploading" class="relative z-[1] flex flex-col items-center justify-center text-black dark:text-gray-600">
      <div class="text-[14px] laptop:text-[16px]">Uploading</div>
      <div class="font-feature-slashed-zero flex h-[30px] items-center gap-x-xs font-bold laptop:h-[45px] laptop:text-[32px]">
        <span>{{ uploadedCount }}</span>
        <span>/</span>
        <span>{{ allCount }}</span>
      </div>
      <div class="text-[14px] laptop:text-[16px]">files</div>
    </div>

    <div v-if="uploading" class="main-drafts__upload__progress z-[0] bg-gray-250 dark:bg-gray-900">
      <div class="main-drafts__upload__progress__indicator bg-gray-100 dark:bg-gray-975" :style="{ left: progress + '%' }"></div>
    </div>
  </div>
</template>

<style lang="scss">
.drag-zone {
  pointer-events: none;
  opacity: 0;
  z-index: 1003;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  transition: opacity 0.15s linear;
  cursor: grabbing;

  &.show {
    opacity: 1;
    pointer-events: all;
  }

  .cloud {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    pointer-events: none;
  }
}

.main-drafts__upload {
  background-image: repeating-linear-gradient(0deg, #b7b7b7, #b7b7b7 2px, transparent 2px, transparent 4px, #b7b7b7 4px),
    repeating-linear-gradient(90deg, #b7b7b7, #b7b7b7 2px, transparent 2px, transparent 4px, #b7b7b7 4px),
    repeating-linear-gradient(180deg, #b7b7b7, #b7b7b7 2px, transparent 2px, transparent 4px, #b7b7b7 4px),
    repeating-linear-gradient(270deg, #b7b7b7, #b7b7b7 2px, transparent 2px, transparent 4px, #b7b7b7 4px);
  background-size:
    8px 100%,
    100% 8px,
    8px 100%,
    100% 8px;
  background-position:
    0 0,
    0 0,
    100% 0,
    0 100%;
  background-repeat: no-repeat;
  display: grid;
  grid-template-rows: 1fr auto;
  break-inside: avoid;
  border-radius: 12px;
  position: relative;
  user-select: none;

  input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
  }

  &:before {
    content: '';
    position: absolute;
    top: 1px;
    left: 1px;
    bottom: 1px;
    right: 1px;
    border-radius: 10px;
    background: #fcfcfc;
    transition-property: background;
    transition-duration: 100ms;
    transition-timing-function: linear;
  }

  &:not(.uploading):hover {
    cursor: pointer;
    background-image: none;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15);

    &:before {
      background: white;
    }
  }

  &:not(.uploading):active {
    cursor: pointer;
    background-image: none;
    box-shadow: none;

    &:before {
      background: #f1f1f1;
    }
  }

  &__progress {
    position: absolute;
    top: 1px;
    left: 1px;
    bottom: 1px;
    right: 1px;
    border-radius: 10px;
    overflow: hidden;

    &__indicator {
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      transition: left 0.1s linear;
    }
  }
}

.dark .main-drafts__upload {
  background-image: repeating-linear-gradient(0deg, #313131, #313131 2px, transparent 2px, transparent 4px, #313131 4px),
    repeating-linear-gradient(90deg, #313131, #313131 2px, transparent 2px, transparent 4px, #313131 4px),
    repeating-linear-gradient(180deg, #313131, #313131 2px, transparent 2px, transparent 4px, #313131 4px),
    repeating-linear-gradient(270deg, #313131, #313131 2px, transparent 2px, transparent 4px, #313131 4px);

  &:before {
    background: #131313;
  }

  &:not(.uploading):hover {
    &:before {
      background: #282828;
    }
  }

  &:not(.uploading):active {
    &:before {
      background: #181818;
    }
  }
}
</style>
